import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import { subscriptionStyle, LOGO_SRC, fmt } from "./sharedStyle";
import SquareCardForm from "./SquareCardForm";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export default function AccountPage() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [account, setAccount] = useState(null);
  const [products, setProducts] = useState([]);
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardSubmitting, setCardSubmitting] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [cardSuccess, setCardSuccess] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Resubscribe item picker (used when reactivating a canceled subscription)
  const [resubQty, setResubQty] = useState({});
  const [resubSeeded, setResubSeeded] = useState(false);

  const loadAccount = () => {
    setLoading(true);
    fetch("/api/subscription-account")
      .then(async (res) => {
        if (res.status === 401) {
          window.location.href = "/subscription/access";
          return null;
        }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Couldn't load your account.");
        return data;
      })
      .then((data) => {
        if (data) setAccount(data);
      })
      .catch((err) => setLoadError(err.message || "Couldn't load your account."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAccount();
    supabase
      .from("products")
      .select("*")
      .eq("visible", true)
      .order("sort_order")
      .then(({ data }) => setProducts(data || []));
  }, []);

  // Seed the resubscribe picker from the subscription's existing base items
  // (matched by name — base_items don't store a product id) the first time
  // we see a reactivatable canceled subscription, so editing starts from
  // what they already had instead of empty.
  useEffect(() => {
    if (resubSeeded || products.length === 0) return;
    if (account?.subscription?.status === "canceled" && account?.canReverseCancel) {
      const seeded = {};
      (account.subscription.baseItems || []).forEach((item) => {
        const p = products.find((p) => p.name === item.name);
        if (p) seeded[String(p.id)] = item.quantity;
      });
      setResubQty(seeded);
      setResubSeeded(true);
    }
  }, [account, products, resubSeeded]);

  const setResubItemQty = (id, qty) => {
    setResubQty((prev) => {
      const next = { ...prev };
      const key = String(id);
      if (qty <= 0) delete next[key];
      else next[key] = qty;
      return next;
    });
  };

  const resubItems = Object.entries(resubQty)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const p = products.find((p) => String(p.id) === id);
      return p ? { name: p.name, quantity: qty, price: p.price } : null;
    })
    .filter(Boolean);
  const resubTotal = resubItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  // "Your last day to gather your goods" — shown before the cancel button is
  // even clicked, so it's an informed decision. If this week's cycle hasn't
  // been charged yet, canceling now also cancels this week, so the last
  // order that actually goes out is last week's (a week before the current
  // cycle's pickup date). If it's already charged, this week is happening
  // regardless, so that's the last day.
  const lastGatherDayLabel = (() => {
    if (!account?.currentCycle) return null;
    if (account.cycleAlreadyCharged) return account.currentCycle.weekOfLabel;
    const d = new Date(`${account.currentCycle.weekOf}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 7);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
  })();

  const handleTokenize = async (token, tokenError) => {
    if (tokenError) { setCardError(tokenError); return; }
    if (!token) return;
    setCardSubmitting(true);
    setCardError(null);
    try {
      const res = await fetch("/api/subscription-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update-card", cardNonce: token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Couldn't save that card.");
      setCardSuccess("Your card on file has been updated.");
      setShowCardForm(false);
      loadAccount();
    } catch (err) {
      setCardError(err.message || "Something went wrong. Please try again.");
    } finally {
      setCardSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel your weekly basket?")) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await fetch("/api/subscription-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cancel" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      setActionMessage(
        data.cycleCanceled
          ? "Your weekly basket has been canceled and this week's order won't be charged."
          : "Your weekly basket has been canceled — this week's order was already charged and will still go through, but no future weeks will be created."
      );
      setResubQty({});
      setResubSeeded(false);
      loadAccount();
    } catch (err) {
      setActionError(err.message || "Something went wrong. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReverseCancel = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await fetch("/api/subscription-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reverse-cancel", baseItems: resubItems }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      setActionMessage("Your weekly basket is active again.");
      setResubSeeded(false);
      loadAccount();
    } catch (err) {
      setActionError(err.message || "Something went wrong. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="gg-app">
      <style>{subscriptionStyle}</style>
      <header className="gg-header">
        <div className="gg-logo"><img src={LOGO_SRC} alt="Grind and Gather" /></div>
        <a href="mailto:grindgather@gmail.com" className="gg-contact-link">Get in Touch</a>
      </header>

      <div className="gg-page">
        <h1>My Weekly Basket</h1>

        {loading && <p className="gg-loading">Loading your account…</p>}
        {!loading && loadError && <div className="gg-error">{loadError}</div>}

        {!loading && account && (
          <>
            <p>
              Signed in as {account.subscription.customerName} ({account.subscription.email}) — weekly basket is{" "}
              <strong>{account.subscription.status}</strong>.
            </p>

            {actionMessage && <div className="gg-success">{actionMessage}</div>}
            {actionError && <div className="gg-error">{actionError}</div>}

            <div className="gg-card">
              <h2>Base Order</h2>
              {account.subscription.baseItems.map((item, i) => (
                <div key={i} className="gg-item-row">
                  <span>{item.name} × {item.quantity}</span>
                  <span>{fmt(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            {account.currentCycle && (
              <div className="gg-card">
                <h2>This Week</h2>
                <p style={{ marginBottom: 0 }}>
                  Pickup {account.currentCycle.weekOfLabel} — status: <strong>{account.currentCycle.status}</strong>
                </p>
              </div>
            )}

            <div className="gg-card">
              <h2>Card on File</h2>
              <p>
                {account.subscription.cardBrand && account.subscription.cardLast4
                  ? `${account.subscription.cardBrand} ending in ${account.subscription.cardLast4}`
                  : "A card is on file."}
              </p>
              {!showCardForm ? (
                <button className="gg-btn-outline" onClick={() => { setShowCardForm(true); setCardSuccess(null); }}>
                  Update Card
                </button>
              ) : (
                <>
                  <SquareCardForm onTokenize={handleTokenize} loading={cardSubmitting} error={cardError} buttonLabel="Save New Card" />
                  <button className="gg-btn-outline" style={{ marginTop: 12 }} onClick={() => setShowCardForm(false)}>Cancel</button>
                </>
              )}
              {cardSuccess && <div className="gg-success">{cardSuccess}</div>}
            </div>

            <div className="gg-section-divider" />

            {account.subscription.status === "active" && account.canCancel && (
              <div className="gg-card">
                <h2>Want to stop your weekly basket entirely? Cancel anytime!</h2>
                {lastGatherDayLabel && (
                  <p>Your last day to gather your goods: <strong>{lastGatherDayLabel}</strong></p>
                )}
                <button className="gg-btn-danger" disabled={actionLoading} onClick={handleCancel}>
                  {actionLoading ? "Working…" : "I'm Done for Now"}
                </button>
              </div>
            )}

            {account.subscription.status === "canceled" && account.canReverseCancel && (
              <div className="gg-card">
                <h2>Changed Your Mind?</h2>
                <p>It's still before Saturday 10pm — you can resubscribe for this week. Adjust your weekly items below if you'd like, or leave them as they were.</p>

                {products.map((p) => {
                  const qty = resubQty[String(p.id)] || 0;
                  return (
                    <div key={p.id} className="gg-item-row">
                      <span>{p.name} — {fmt(p.price)}</span>
                      <div className="gg-qty-control">
                        <button className="gg-qty-btn" onClick={() => setResubItemQty(p.id, qty - 1)} disabled={qty === 0}>−</button>
                        <span style={{ minWidth: 18, textAlign: "center" }}>{qty}</span>
                        <button className="gg-qty-btn" onClick={() => setResubItemQty(p.id, qty + 1)}>+</button>
                      </div>
                    </div>
                  );
                })}
                {resubItems.length > 0 && (
                  <div className="gg-item-row" style={{ fontWeight: 500, marginTop: 8 }}>
                    <span>Weekly Total</span>
                    <span>{fmt(resubTotal)}</span>
                  </div>
                )}

                <button
                  className="gg-btn"
                  style={{ marginTop: 16 }}
                  disabled={actionLoading || resubItems.length === 0}
                  onClick={handleReverseCancel}
                >
                  {actionLoading ? "Working…" : "Reactivate Weekly Basket"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
